// Epic Discovery Modal - The "WOW" moment!
import React, { useState, useEffect } from 'react';
import { ElementDef } from '../../store/elementStore';
import { colors } from '../../theme/designTokens';
import ParticleSystem from '../effects/ParticleSystem';
import Enhanced3DElement from '../Enhanced3DElement';
import ShareButton from './ShareButton';
import { playSound } from '../../services/soundService';
import { triggerHaptic } from '../../services/capacitorService';

interface DiscoveryModalProps {
  element: ElementDef | null;
  isOpen: boolean;
  onClose: () => void;
  isFirstDiscovery?: boolean;
}

const DiscoveryModal: React.FC<DiscoveryModalProps> = ({
  element,
  isOpen,
  onClose,
  isFirstDiscovery = false,
}) => {
  const [showParticles, setShowParticles] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'enter' | 'reveal' | 'celebrate'>('enter');

  useEffect(() => {
    if (isOpen && element) {
      // Trigger haptic feedback
      triggerHaptic('heavy');

      // Play discovery sound
      playSound('discover');

      // Animation sequence
      setAnimationPhase('enter');
      setShowParticles(true);

      setTimeout(() => setAnimationPhase('reveal'), 300);
      setTimeout(() => setAnimationPhase('celebrate'), 800);

      // Auto-close after celebration (optional)
      // setTimeout(onClose, 5000);
    }
  }, [isOpen, element]);

  if (!isOpen || !element) return null;

  const tierColors = colors.elementTiers[element.tier as keyof typeof colors.elementTiers] || colors.elementTiers[1];

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: `linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(26,31,58,0.95) 50%, rgba(45,27,78,0.95) 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9998,
    backdropFilter: 'blur(20px)',
    opacity: animationPhase === 'enter' ? 0 : 1,
    transition: 'opacity 0.3s ease',
  };

  const modalStyle: React.CSSProperties = {
    position: 'relative',
    maxWidth: '90vw',
    width: '500px',
    padding: '3rem',
    borderRadius: '2rem',
    background: `linear-gradient(135deg, ${colors.background.card} 0%, ${colors.background.secondary} 50%, ${colors.background.primary} 100%)`,
    border: `3px solid ${tierColors.color}`,
    boxShadow: `0 0 80px ${tierColors.color}90, 0 20px 60px rgba(0,0,0,0.6), inset 0 0 60px ${tierColors.color}10`,
    textAlign: 'center',
    transform: animationPhase === 'enter' ? 'scale(0.8)' : 'scale(1)',
    opacity: animationPhase === 'enter' ? 0 : 1,
    transition: 'all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: isFirstDiscovery ? '2.5rem' : '2rem',
    fontWeight: 800,
    background: colors.gradients.legendary,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '1rem',
    textShadow: '0 0 30px rgba(255, 215, 0, 0.5)',
    animation: 'scaleIn 0.6s ease-out',
  };

  const elementContainerStyle: React.CSSProperties = {
    margin: '2rem 0',
    display: 'flex',
    justifyContent: 'center',
    animation: animationPhase === 'celebrate' ? 'float 3s ease-in-out infinite' : 'scaleIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  };

  const tierLabelStyle: React.CSSProperties = {
    display: 'inline-block',
    padding: '0.5rem 1.5rem',
    borderRadius: '2rem',
    background: `linear-gradient(135deg, ${tierColors.color}, ${tierColors.glow})`,
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: 700,
    marginBottom: '1rem',
    boxShadow: `0 4px 12px ${tierColors.color}50`,
  };

  const getTierName = (tier: number): string => {
    const names = ['', 'Basic', 'Common', 'Uncommon', 'Rare', 'Epic', 'Legendary', 'Mythic'];
    return names[tier] || 'Unknown';
  };

  const descriptionStyle: React.CSSProperties = {
    color: colors.text.secondary,
    fontSize: '1.1rem',
    lineHeight: 1.6,
    marginBottom: '2rem',
    maxWidth: '400px',
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '1rem 2.5rem',
    fontSize: '1.1rem',
    fontWeight: 700,
    background: colors.gradients.primary,
    color: 'white',
    border: 'none',
    borderRadius: '2rem',
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(101, 163, 13, 0.4)',
    transition: 'all 0.3s ease',
    marginTop: '1rem',
  };

  return (
    <>
      {/* Particles */}
      <ParticleSystem
        trigger={showParticles}
        type="celebration"
        onComplete={() => setShowParticles(false)}
      />

      {/* Modal Overlay */}
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Animated background glow */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '200%',
              height: '200%',
              background: `radial-gradient(circle, ${tierColors.color}20 0%, transparent 70%)`,
              animation: 'pulse 3s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Title */}
            <h2 style={titleStyle}>
              {isFirstDiscovery ? '🌟 FIRST DISCOVERY! 🌟' : '✨ NEW DISCOVERY! ✨'}
            </h2>

            {/* Tier Badge */}
            <div style={tierLabelStyle}>
              {getTierName(element.tier)} • Tier {element.tier}
            </div>

            {/* Element Display */}
            <div style={elementContainerStyle}>
              <Enhanced3DElement
                e={element}
                size={160}
                intensity="intense"
              />
            </div>

            {/* Element Name */}
            <h3
              style={{
                fontSize: '2rem',
                fontWeight: 700,
                color: colors.text.primary,
                marginBottom: '0.5rem',
                textShadow: `0 0 20px ${tierColors.color}50`,
              }}
            >
              {element.name}
            </h3>

            {/* Description */}
            <p style={descriptionStyle}>
              {isFirstDiscovery
                ? `Congratulations on discovering your first element! ${element.name} is the beginning of your alchemical journey.`
                : `You've unlocked a new ${getTierName(element.tier).toLowerCase()} element! Continue combining to discover more.`}
            </p>

            {/* Stats */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '2rem',
                marginBottom: '2rem',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ color: colors.text.tertiary, fontSize: '0.8rem' }}>Tier</div>
                <div style={{ color: tierColors.color, fontSize: '1.5rem', fontWeight: 700 }}>
                  {element.tier}
                </div>
              </div>
              <div>
                <div style={{ color: colors.text.tertiary, fontSize: '0.8rem' }}>Type</div>
                <div style={{ color: tierColors.color, fontSize: '1.5rem', fontWeight: 700 }}>
                  {getTierName(element.tier)}
                </div>
              </div>
            </div>

            {/* Share Button */}
            <div style={{ marginBottom: '1rem' }}>
              <ShareButton
                title={`I discovered ${element.name}!`}
                text={`🎉 I just discovered ${element.name} (Tier ${element.tier} - ${getTierName(element.tier)}) in Alchemistry! Can you unlock all elements? 🔮✨`}
                size="large"
                variant="primary"
              />
            </div>

            {/* Continue Button */}
            <button
              style={buttonStyle}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(101, 163, 13, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(101, 163, 13, 0.4)';
              }}
            >
              Continue Crafting ✨
            </button>

            {/* Close button (X) */}
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: colors.text.secondary,
                fontSize: '1.5rem',
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'rotate(0deg)';
              }}
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DiscoveryModal;
